<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { Dropdown as VDropdown } from 'floating-vue';
import {
	SuiModal,
	SuiButton,
	SuiInput,
	SuiTextarea,
	SuiSelect,
	SuiIcon,
	SuiLoadingCircle,
	SuiInputVariant,
	type SelectItem,
} from '@developers/scoro-ui';
import AppAutoSuggest from '@/features/App/AppAutoSuggest/AutoSuggest.vue';
import AppConfirmationPopover from '@/features/App/AppConfirmationPopover.vue';
import { useTranslation } from '@/composables';
import { AppTranslatedTextWithHTML } from '@/features/App';
import {
	useAvalaraMandatesLazyQuery,
	AvalaraMandatesDocument,
	useAvalaraMappingFieldsLazyQuery,
	AvalaraMappingFieldsDocument,
	useAvalaraScoroFieldsLazyQuery,
	AvalaraScoroFieldsDocument,
	useAvalaraDefaultFieldMappingsLazyQuery,
	AvalaraDefaultFieldMappingsDocument,
	useBookmarksDropdownLazyQuery,
	BookmarksDropdownDocument,
} from '@/generated/graphql';

export type FieldValueMapping = {
	scoroValue: string;
	newValue: string;
};

export type FieldMappingData = {
	fieldId: string;
	fieldLabel: string;
	valueMappings: FieldValueMapping[];
};

export type FieldMappingOption = {
	id: string;
	name: string;
	fieldMappings?: FieldMappingData[];
};

export type ActivationStatus = {
	isActive: boolean;
	statusCode: string;
	statusText: string;
	lastChecked?: string;
};

export type MandateFormData = {
	id?: string;
	dbId?: string;
	name: string;
	mandateCode: string;
	description: string;
	bookmarkId?: string | null;
	bookmarkName?: string | null;
	fieldMappings: SavedFieldMapping[];
	fieldMappingId?: string | null;
	activationStatus?: ActivationStatus | null;
};

export type SavedFieldMapping = {
	avalaraFieldId?: string;
	avalaraField?: string;
	avalaraFieldLabel?: string;
	scoroField: string;
};

export type FieldMapping = {
	uiKey: string;
	avalaraFieldId: string;
	avalaraFieldLabel: string;
	description: string;
	type: string;
	optionality: string;
	scoroField: string;
	isRequired: boolean;
	isExpanded: boolean;
};

export type AvalaraMandateModalProps = {
	isOpen: boolean;
	module?: string;
	editingMandate?: MandateFormData | null;
	existingMandates?: Array<
		Pick<MandateFormData, 'id' | 'dbId' | 'name'> &
			Partial<Pick<MandateFormData, 'mandateCode'>>
	>;
	companyName?: string;
	companyDisplayName?: string;
	isSandbox?: boolean;
	fieldMappingOptions?: FieldMappingOption[];
};

export type DeleteMandatePayload = {
	id: string;
	dbId?: string;
};

const props = withDefaults(defineProps<AvalaraMandateModalProps>(), {
	module: 'invoices',
	editingMandate: null,
	existingMandates: () => [],
	companyName: '',
	companyDisplayName: '',
	isSandbox: false,
	fieldMappingOptions: () => [],
});

const { t, hasTranslation } = useTranslation();

const MODAL_FADE_DURATION_MS = 150;
const modalVisible = ref(false);
const modalPhase = ref<'closed' | 'opening' | 'open' | 'closing'>('closed');
let closeTimeoutId: number | null = null;

const {
	loading: isMandatesLoading,
	load: loadMandates,
	onResult: onMandatesResult,
} = useAvalaraMandatesLazyQuery({
	countryCode: '',
});

const {
	loading: isScoroFieldsLoading,
	load: loadScoroFields,
	onResult: onScoroFieldsResult,
} = useAvalaraScoroFieldsLazyQuery({
	module: props.module,
});

const {
	load: loadDefaultFieldMappings,
	onResult: onDefaultFieldMappingsResult,
} = useAvalaraDefaultFieldMappingsLazyQuery({
	module: props.module,
});

const defaultScoroFieldByAvalaraFieldId = ref<Record<string, string>>({});
const defaultMappingsFetched = ref(false);

type ScoroFieldOption = SelectItem & {
	group?: string | null;
	isGroupHeader?: boolean;
};

const scoroFieldOptions = ref<ScoroFieldOption[]>([]);
const scoroFieldsFetched = ref(false);

const {
	loading: isBookmarksLoading,
	load: loadBookmarks,
	refetch: refetchBookmarks,
	onResult: onBookmarksResult,
} = useBookmarksDropdownLazyQuery(
	{
		filters: {
			modules: [props.module],
			isShared: true,
		},
	},
	{
		fetchPolicy: 'network-only',
	}
);

const bookmarkOptions = ref<SelectItem[]>([]);
const bookmarksFetched = ref(false);
const hasBookmarksBeenLoaded = ref(false);
const mandatesFetched = ref(false);

const BOOKMARKS_RELOAD_DEBOUNCE_MS = 200;
let bookmarksReloadTimeoutId: number | null = null;

const normalizeBookmarksToOptions = (
	bookmarks:
		| Array<{
				id: string;
				name: string;
		  }>
		| undefined
		| null
): SelectItem[] => {
	if (!bookmarks) {
		return [];
	}

	return bookmarks
		.map((bookmark) => ({
			id: bookmark.id,
			title: bookmark.name,
		}))
		.sort((a, b) => a.title.localeCompare(b.title));
};

const isLoadingInitialData = ref(false);
const pendingFormData = ref<MandateFormData | null>(null);

const savedScoroFieldByAvalaraFieldId = ref<Record<string, string>>({});

const emit = defineEmits<{
	(event: 'close'): void;
	(event: 'save', payload: MandateFormData): void;
	(event: 'delete', payload: DeleteMandatePayload): void;
}>();

const mandateName = ref('');
const mandateCode = ref<string | undefined>(undefined);
const description = ref('');
const bookmarkId = ref<string | undefined>(undefined);
const selectedFieldMappingId = ref<string | undefined>(undefined);
const isSaving = ref(false);
const hasAttemptedSave = ref(false);

const isDescriptionLabelVisible = computed(() => {
	return description.value.trim() !== '';
});

const isNameLabelVisible = computed(() => {
	return mandateName.value.trim() !== '';
});

const isMandateLabelVisible = computed(() => {
	return mandateCode.value !== undefined && mandateCode.value !== '';
});

const isNameEmpty = computed(() => {
	return mandateName.value.trim() === '';
});

const fieldMappings = ref<FieldMapping[]>([]);
const editingMandateId = ref<string | undefined>(undefined);
const mandateDbId = ref<string | undefined>(undefined);

const customProperties = ref<string[]>([]);

const DEFAULT_DOCUMENT_TYPE = 'ubl-invoice';
const DEFAULT_DOCUMENT_VERSION = '2.1';
const EXCLUDED_SCORO_FIELD_GROUPS = new Set(['search options']);
const BILLS_RESTRICTED_GROUP_BILL = 'bill';
const BILLS_RESTRICTED_GROUP_BILL_LINE = 'bill line';
const BILLS_ALLOWED_BILL_FIELD_IDS = new Set(['description', 'reference_no']);
const BILLS_ALLOWED_BILL_LINE_FIELD_IDS = new Set(['comment', 'comment2']);

function isExcludedScoroFieldGroup(group: unknown): boolean {
	return EXCLUDED_SCORO_FIELD_GROUPS.has(
		String(group ?? '')
			.trim()
			.toLocaleLowerCase()
	);
}

function shouldKeepBillsRestrictedGroupField(field: {
	id?: string | null;
	group?: string | null;
}): boolean {
	const normalizedGroup = String(field.group ?? '')
		.trim()
		.toLocaleLowerCase();
	if (
		normalizedGroup !== BILLS_RESTRICTED_GROUP_BILL &&
		normalizedGroup !== BILLS_RESTRICTED_GROUP_BILL_LINE
	) {
		return true;
	}

	const id = String(field.id ?? '').trim();
	if (id === '') {
		return false;
	}

	const fieldId = id.includes('.') ? (id.split('.').pop() ?? '') : id;

	if (fieldId.startsWith('c_') || fieldId.startsWith('custom_')) {
		return true;
	}

	if (normalizedGroup === BILLS_RESTRICTED_GROUP_BILL) {
		return BILLS_ALLOWED_BILL_FIELD_IDS.has(fieldId);
	}

	return BILLS_ALLOWED_BILL_LINE_FIELD_IDS.has(fieldId);
}

type MandateOption = SelectItem & {
	mandateId: string;
	isActive?: boolean;
	statusCode?: string;
	statusText?: string;
};
const mandateOptions = ref<MandateOption[]>([]);
const activationStatus = ref<ActivationStatus | null>(null);

const syncActivationStatusWithSelectedMandate = (): void => {
	const selectedMandateCode = String(mandateCode.value ?? '').trim();
	if (selectedMandateCode === '') {
		activationStatus.value = null;
		return;
	}

	const mandate = mandateOptions.value.find(
		(option) => String(option.id ?? '').trim() === selectedMandateCode
	);

	if (!mandate) {
		return;
	}

	const isActive = Boolean(mandate.isActive);
	activationStatus.value = {
		isActive,
		statusCode: String(mandate.statusCode ?? '').trim() || 'Unknown',
		statusText: isActive ? 'Active' : 'Inactive',
	};
};

// Mapping of Avalara field IDs to field mapping field IDs
// Keys are partial matches - if an Avalara field ID contains the key, it matches
const FIELD_MAPPING_AUTO_POPULATE: Array<{
	pattern: string;
	fieldMappingId: string;
}> = [
	{ pattern: 'InvoicedQuantity/@unitCode', fieldMappingId: 'unit' },
	{ pattern: 'unitCode', fieldMappingId: 'unit' },
	{ pattern: 'TaxCategory/cbc:ID', fieldMappingId: 'tax_category' },
	{ pattern: 'InvoiceTypeCode', fieldMappingId: 'document_type' },
	{ pattern: 'PaymentMeansCode/@name', fieldMappingId: 'payment_method' },
	{ pattern: 'PaymentMeansCode', fieldMappingId: 'payment_method' },
];

const selectedFieldMappingData = computed<FieldMappingOption | undefined>(
	() => {
		if (!selectedFieldMappingId.value) {
			return undefined;
		}
		return props.fieldMappingOptions.find(
			(fm) => fm.id === selectedFieldMappingId.value
		);
	}
);

const groupedScoroFieldOptions = computed(() => {
	const groups: Array<{ group: string; fields: ScoroFieldOption[] }> = [];
	const groupMap = new Map<string, ScoroFieldOption[]>();
	const groupOrder: string[] = [];

	for (const option of scoroFieldOptions.value) {
		if (option.isGroupHeader) {
			continue;
		}
		const group = option.group ?? '';
		if (!groupMap.has(group)) {
			groupMap.set(group, []);
			groupOrder.push(group);
		}
		groupMap.get(group)?.push(option);
	}

	for (const group of groupOrder) {
		const fields = groupMap.get(group) ?? [];
		if (fields.length > 0) {
			groups.push({ group, fields });
		}
	}

	// Add field mapping group (before Custom properties)
	const fieldMapping = selectedFieldMappingData.value;
	const isBillsModule = props.module === 'bills';
	if (!isBillsModule && fieldMapping && fieldMapping.fieldMappings) {
		const fieldMappingFields: ScoroFieldOption[] = [];
		for (const fm of fieldMapping.fieldMappings) {
			// Show field types (Unit, Tax Category, etc.), not individual values
			if (fm.fieldLabel && fm.fieldLabel.trim() !== '') {
				fieldMappingFields.push({
					id: `fieldmapping:${fm.fieldId}`,
					title: fm.fieldLabel,
				});
			}
		}
		if (fieldMappingFields.length > 0) {
			groups.push({
				group: fieldMapping.name,
				fields: fieldMappingFields,
			});
		}
	}

	if (customProperties.value.length > 0) {
		groups.push({
			group: t('avalara_custom_properties'),
			fields: customProperties.value.map((name) => ({
				id: `custom:${name}`,
				title: name,
			})),
		});
	}

	return groups;
});

/**
 * Find the actual field ID in the Scoro field options that matches the given default value.
 * This handles cases where the default uses a group prefix but the options might have different formats.
 */
function findMatchingScoroFieldId(defaultValue: string): string | undefined {
	// First try exact match
	for (const groupData of groupedScoroFieldOptions.value) {
		const field = groupData.fields.find((f) => f.id === defaultValue);
		if (field) {
			return String(field.id);
		}
	}
	if (defaultValue.includes('.')) {
		const parts = defaultValue.split('.');
		const groupName = parts[0];
		const fieldName = parts.slice(1).join('.');
		// Look in the specific group first
		const groupData = groupedScoroFieldOptions.value.find(
			(g) => g.group === groupName
		);
		if (groupData) {
			const field = groupData.fields.find((f) => {
				const fName =
					String(f.id ?? '')
						.split('.')
						.pop() ?? '';
				return fName === fieldName;
			});
			if (field) {
				return String(field.id);
			}
		}
	}
	return undefined;
}

/**
 * Re-process existing field mappings to find matching Scoro field IDs.
 * This is called after Scoro fields are loaded to update any scoroField values
 * that couldn't be matched earlier.
 */
function normalizeFieldMappingScoroFields(): void {
	for (let index = 0; index < fieldMappings.value.length; index++) {
		const currentScoroField = fieldMappings.value[index].scoroField;
		if (!currentScoroField) {
			continue;
		}
		// Try to find a matching field ID
		const matchingFieldId = findMatchingScoroFieldId(currentScoroField);
		if (matchingFieldId && matchingFieldId !== currentScoroField) {
			fieldMappings.value[index].scoroField = matchingFieldId;
		}
	}
}

/**
 * Auto-populate Avalara fields with corresponding field mapping options.
 * This is called when field mappings are loaded or when a field mapping is selected.
 */
function autoPopulateFieldMappingFields(): void {
	if (props.module === 'bills') {
		return;
	}

	const fieldMappingId = selectedFieldMappingId.value;
	if (!fieldMappingId || fieldMappingId === '') {
		return;
	}

	const selectedFm = props.fieldMappingOptions.find(
		(fm) => fm.id === fieldMappingId
	);
	if (!selectedFm || !selectedFm.fieldMappings) {
		return;
	}

	// Get available field mapping field IDs from the selected field mapping
	const availableFieldIds = new Set(
		selectedFm.fieldMappings.map((fm) => fm.fieldId)
	);

	for (const mapping of fieldMappings.value) {
		// Skip if already has a value
		if (mapping.scoroField && mapping.scoroField !== '') {
			continue;
		}

		// Find matching pattern for this Avalara field
		const matchingPattern = FIELD_MAPPING_AUTO_POPULATE.find((p) =>
			mapping.avalaraFieldId.includes(p.pattern)
		);

		if (
			matchingPattern &&
			availableFieldIds.has(matchingPattern.fieldMappingId)
		) {
			mapping.scoroField = `fieldmapping:${matchingPattern.fieldMappingId}`;
		}
	}
}

type MappingsViewFilter = 'all' | 'required' | 'conditional' | 'optional';
const mappingsViewFilter = ref<MappingsViewFilter>('required');
const mappingsSearchQuery = ref('');
const modalContentRef = ref<HTMLElement | null>(null);

let modalResizeObserver: ResizeObserver | null = null;

onMounted(() => {
	modalResizeObserver = new ResizeObserver(() => {
		const trigger = () => {
			window.dispatchEvent(new Event('resize'));
			window.dispatchEvent(new Event('scroll'));
		};
		trigger();
		// Some libraries need a few frames to settle after DOM updates
		requestAnimationFrame(trigger);
		setTimeout(trigger, 100);
		setTimeout(trigger, 500);
	});
});

onBeforeUnmount(() => {
	modalResizeObserver?.disconnect();
});

watch(
	modalContentRef,
	(el) => {
		if (el) {
			modalResizeObserver?.observe(el);
		} else {
			modalResizeObserver?.disconnect();
		}
	},
	{ immediate: true }
);

function setMappingsViewFilter(
	filter: MappingsViewFilter,
	hideDropdown?: () => void
): void {
	mappingsViewFilter.value = filter;
	hideDropdown?.();
	(document.activeElement as HTMLElement | null)?.blur?.();
}

const isNameInvalid = computed(() => {
	return hasAttemptedSave.value && mandateName.value.trim() === '';
});

const isNameDuplicate = computed(() => {
	if (!hasAttemptedSave.value) {
		return false;
	}

	const name = mandateName.value.trim();
	if (name === '') {
		return false;
	}

	const normalized = name.toLocaleLowerCase();

	return (props.existingMandates ?? []).some((mandate) => {
		const existingName = String(mandate?.name ?? '')
			.trim()
			.toLocaleLowerCase();
		if (existingName === '' || existingName !== normalized) {
			return false;
		}

		const sameById =
			mandate?.id !== undefined &&
			editingMandateId.value !== undefined &&
			mandate.id === editingMandateId.value;
		const sameByDbId =
			mandate?.dbId !== undefined &&
			mandateDbId.value !== undefined &&
			mandate.dbId === mandateDbId.value;

		return !sameById && !sameByDbId;
	});
});

const nameErrorMessage = computed(() => {
	if (isNameInvalid.value) {
		return t('avalara_mandate_name_required');
	}
	if (isNameDuplicate.value) {
		return t('avalara_mandate_name_unique');
	}
	return undefined;
});

const isMandateEmpty = computed(() => {
	return mandateCode.value === undefined || mandateCode.value === '';
});

const BILL_MANDATE_ALREADY_USED_LANGVAR =
	'avalara_mandate_used_for_bills_mapping';

const billMandateAlreadyUsedMessage = computed(() => {
	if (hasTranslation(BILL_MANDATE_ALREADY_USED_LANGVAR)) {
		return t(BILL_MANDATE_ALREADY_USED_LANGVAR);
	}
	return 'This mandate is already used for bills mapping.';
});

const normalizeOptionality = (optionality: unknown): string => {
	return String(optionality ?? '')
		.trim()
		.toLowerCase();
};

const mappingsViewFilterLabel = computed(() => {
	switch (mappingsViewFilter.value) {
		case 'required':
			return t('required');
		case 'conditional':
			return t('conditional');
		case 'optional':
			return t('optional');
		case 'all':
		default:
			return t('all');
	}
});

const visibleFieldMappings = computed(() => {
	const query = mappingsSearchQuery.value.toLowerCase().trim();
	return fieldMappings.value.filter((mapping) => {
		const optionality = normalizeOptionality(mapping.optionality);
		const matchesFilter =
			mappingsViewFilter.value === 'all' ||
			optionality === mappingsViewFilter.value;

		if (!matchesFilter) {
			return false;
		}

		if (!query) {
			return true;
		}

		return (
			mapping.avalaraFieldLabel.toLowerCase().includes(query) ||
			mapping.description.toLowerCase().includes(query)
		);
	});
});

const generateDbId = (): string => {
	if (
		typeof crypto !== 'undefined' &&
		typeof crypto.randomUUID === 'function'
	) {
		return crypto.randomUUID();
	}
	return `mandate_${Date.now()}_${Math.random().toString(36).slice(2)}`;
};

function populateFormInternal(mandate: MandateFormData): void {
	mandateName.value = mandate.name;
	description.value = mandate.description;
	bookmarkId.value = mandate.bookmarkId
		? String(mandate.bookmarkId)
		: undefined;
	selectedFieldMappingId.value = mandate.fieldMappingId
		? String(mandate.fieldMappingId)
		: undefined;
	editingMandateId.value = mandate.id;
	mandateDbId.value = mandate.dbId ?? mandate.id;
	activationStatus.value = mandate.activationStatus ?? null;

	const extractedCustomProperties: string[] = [];
	savedScoroFieldByAvalaraFieldId.value = mandate.fieldMappings.reduce<
		Record<string, string>
	>((acc, mapping) => {
		const avalaraFieldId = String(
			mapping?.avalaraFieldId ?? mapping?.avalaraField ?? ''
		).trim();
		const scoroField = String(mapping?.scoroField ?? '').trim();

		if (avalaraFieldId !== '' && scoroField !== '') {
			acc[avalaraFieldId] = scoroField;

			if (scoroField.startsWith('custom:')) {
				const customName = scoroField.replace('custom:', '');
				if (
					customName &&
					!extractedCustomProperties.includes(customName)
				) {
					extractedCustomProperties.push(customName);
				}
			}
		}

		return acc;
	}, {});

	customProperties.value = extractedCustomProperties;
	mandateCode.value = mandate.mandateCode;
	syncActivationStatusWithSelectedMandate();
}

function tryPopulatePendingForm(): void {
	if (
		mandatesFetched.value &&
		scoroFieldsFetched.value &&
		bookmarksFetched.value
	) {
		isLoadingInitialData.value = false;
		if (pendingFormData.value) {
			populateFormInternal(pendingFormData.value);
			pendingFormData.value = null;
		}
	}
}

const fetchBookmarks = async (): Promise<void> => {
	if (isBookmarksLoading.value) {
		return;
	}

	const variables = {
		filters: {
			modules: [props.module],
			isShared: true,
		},
	};

	if (hasBookmarksBeenLoaded.value) {
		const result = await refetchBookmarks(variables);
		if (result?.data?.BookmarksList) {
			bookmarkOptions.value = normalizeBookmarksToOptions(
				result.data.BookmarksList
			);
			bookmarksFetched.value = true;
			tryPopulatePendingForm();
		}
		return;
	}

	loadBookmarks(BookmarksDropdownDocument, variables);
	hasBookmarksBeenLoaded.value = true;
};

onBookmarksResult((result) => {
	if (result.data?.BookmarksList) {
		bookmarkOptions.value = normalizeBookmarksToOptions(
			result.data.BookmarksList
		);

		bookmarksFetched.value = true;
		tryPopulatePendingForm();
	}
});

onScoroFieldsResult((result) => {
	if (result.data?.AvalaraScoroFieldsList) {
		const fields = result.data.AvalaraScoroFieldsList;
		const groupedFields = new Map<string, ScoroFieldOption[]>();
		const groupOrder: string[] = [];

		for (const field of fields) {
			const group = field.group ?? '';
			if (props.module === 'bills' && isExcludedScoroFieldGroup(group)) {
				continue;
			}
			if (
				props.module === 'bills' &&
				!shouldKeepBillsRestrictedGroupField({
					id: field.id,
					group,
				})
			) {
				continue;
			}

			if (!groupedFields.has(group)) {
				groupedFields.set(group, []);
				groupOrder.push(group);
			}
			groupedFields.get(group)?.push({
				id: field.id,
				title: field.title,
				group: field.group,
			});
		}

		const optionsWithHeaders: ScoroFieldOption[] = [];
		for (const group of groupOrder) {
			const groupFields = groupedFields.get(group) ?? [];
			if (groupFields.length === 0) {
				continue;
			}

			if (group !== '') {
				optionsWithHeaders.push({
					id: `__group_header__${group}`,
					title: group,
					group,
					isGroupHeader: true,
				});
			}

			for (const field of groupFields) {
				optionsWithHeaders.push(field);
			}
		}

		const uniqueById = new Map<string, ScoroFieldOption>();
		for (const option of optionsWithHeaders) {
			const id = String(option.id ?? '');
			if (id === '') {
				continue;
			}
			if (!uniqueById.has(id)) {
				uniqueById.set(id, option);
			}
		}

		scoroFieldOptions.value = Array.from(uniqueById.values());
		scoroFieldsFetched.value = true;
		// Re-normalize field mappings now that Scoro fields are loaded
		normalizeFieldMappingScoroFields();
		tryPopulatePendingForm();
	}
});

const fetchScoroFields = () => {
	if (!scoroFieldsFetched.value && !isScoroFieldsLoading.value) {
		loadScoroFields(AvalaraScoroFieldsDocument, {
			module: props.module,
		});
	}
};

/**
 * Check if a field ID exists in the available Scoro field options.
 * Used to determine if a saved/default field value is actually selectable.
 */
const fieldExistsInOptions = (fieldId: string | undefined): boolean => {
	if (!fieldId) {
		return false;
	}
	for (const groupData of groupedScoroFieldOptions.value) {
		const field = groupData.fields.find((f) => f.id === fieldId);
		if (field) {
			return true;
		}
	}
	return false;
};

function resolveFieldToApply(scoroField: string): string | null {
	if (!scoroField) {
		return null;
	}

	if (scoroField.startsWith('custom:')) {
		return scoroField;
	}

	if (
		scoroField.startsWith('fieldmapping:') &&
		!selectedFieldMappingId.value
	) {
		return null;
	}

	const matchingFieldId = findMatchingScoroFieldId(scoroField);
	const fieldToApply = matchingFieldId ?? scoroField;

	return fieldExistsInOptions(fieldToApply) ? fieldToApply : null;
}

function applySavedMappingsToLoadedFields(): void {
	const saved = savedScoroFieldByAvalaraFieldId.value;
	const defaults = defaultScoroFieldByAvalaraFieldId.value;
	const hasSaved = saved && Object.keys(saved).length > 0;
	const hasDefaults = defaults && Object.keys(defaults).length > 0;

	if (!hasSaved && !hasDefaults) {
		return;
	}

	for (let index = 0; index < fieldMappings.value.length; index++) {
		const avalaraFieldId = fieldMappings.value[index].avalaraFieldId;
		const scoroField = hasSaved
			? saved[avalaraFieldId]
			: defaults[avalaraFieldId];
		const fieldToApply = resolveFieldToApply(scoroField);

		if (!fieldToApply) {
			continue;
		}

		if (
			scoroField.startsWith('custom:') &&
			!customProperties.value.includes(scoroField.replace('custom:', ''))
		) {
			customProperties.value.push(scoroField.replace('custom:', ''));
		}

		fieldMappings.value[index].scoroField = fieldToApply;
	}
}

function applyDefaultMappingsToLoadedFields(): void {
	if (props.module === 'bills') {
		return;
	}

	const saved = savedScoroFieldByAvalaraFieldId.value;
	if (saved && Object.keys(saved).length > 0) {
		return;
	}

	const defaults = defaultScoroFieldByAvalaraFieldId.value;
	if (!defaults || Object.keys(defaults).length === 0) {
		return;
	}

	for (let index = 0; index < fieldMappings.value.length; index++) {
		if (fieldMappings.value[index].scoroField !== '') {
			continue;
		}

		const avalaraFieldId = fieldMappings.value[index].avalaraFieldId;
		const fieldToApply = resolveFieldToApply(defaults[avalaraFieldId]);

		if (fieldToApply) {
			if (
				fieldToApply.startsWith('custom:') &&
				!customProperties.value.includes(
					fieldToApply.replace('custom:', '')
				)
			) {
				customProperties.value.push(
					fieldToApply.replace('custom:', '')
				);
			}
			fieldMappings.value[index].scoroField = fieldToApply;
		}
	}
}

onDefaultFieldMappingsResult((result) => {
	if (props.module === 'bills') {
		defaultScoroFieldByAvalaraFieldId.value = {};
		defaultMappingsFetched.value = true;
		return;
	}

	if (result.data?.AvalaraDefaultFieldMappingsList) {
		defaultScoroFieldByAvalaraFieldId.value =
			result.data.AvalaraDefaultFieldMappingsList.reduce<
				Record<string, string>
			>((acc, mapping) => {
				const avalaraFieldId = String(
					mapping?.avalaraFieldId ?? ''
				).trim();
				const scoroFieldId = String(mapping?.scoroFieldId ?? '').trim();
				if (avalaraFieldId !== '' && scoroFieldId !== '') {
					acc[avalaraFieldId] = scoroFieldId;
				}
				return acc;
			}, {});
		defaultMappingsFetched.value = true;
		applyDefaultMappingsToLoadedFields();
	}
});

const fetchDefaultFieldMappings = () => {
	if (props.module === 'bills') {
		defaultScoroFieldByAvalaraFieldId.value = {};
		defaultMappingsFetched.value = true;
		return;
	}

	if (!defaultMappingsFetched.value) {
		loadDefaultFieldMappings(AvalaraDefaultFieldMappingsDocument, {
			module: props.module,
		});
	}
};

onMandatesResult((result) => {
	if (result.data?.AvalaraMandatesList) {
		mandateOptions.value = result.data.AvalaraMandatesList.map(
			(mandate) => ({
				id: mandate.id,
				title: mandate.title,
				mandateId: mandate.id,
				isActive: mandate.isActive,
				statusCode: mandate.statusCode,
				statusText: mandate.statusText,
			})
		);
		mandatesFetched.value = true;
		syncActivationStatusWithSelectedMandate();
		tryPopulatePendingForm();
	}
});

const fetchMandates = () => {
	if (!mandatesFetched.value && !isMandatesLoading.value) {
		loadMandates(AvalaraMandatesDocument, {
			countryCode: '',
		});
	}
};

const {
	loading: isMappingsLoading,
	load: loadMappings,
	refetch: refetchMappings,
	onResult: onMappingsResult,
} = useAvalaraMappingFieldsLazyQuery(
	{
		mandateId: '',
	},
	{
		fetchPolicy: 'network-only',
	}
);

const hasMappingsBeenLoaded = ref(false);

const selectedMandateId = computed(() => {
	return mandateCode.value ?? '';
});

onMappingsResult((result) => {
	if (result.data?.AvalaraMappingFieldsList) {
		fieldMappings.value = result.data.AvalaraMappingFieldsList.map(
			(field, index) => {
				const optionality = normalizeOptionality(field.optionality);
				const avalaraFieldId =
					field.id || field.path || field.name || '';

				return {
					uiKey: `${String(
						avalaraFieldId || 'avalara-field'
					)}:${optionality}:${index}`,
					avalaraFieldId,
					avalaraFieldLabel:
						field.name || field.path || field.id || '',
					description: field.description || '',
					type: field.dataType || '',
					optionality,
					scoroField: '',
					isRequired: optionality === 'required',
					isExpanded: false,
				};
			}
		);
		applySavedMappingsToLoadedFields();
		autoPopulateFieldMappingFields();
	}
});

const loadMappingsForSelectedMandate = async () => {
	if (!selectedMandateId.value) {
		return;
	}

	fieldMappings.value = [];

	const variables = {
		mandateId: selectedMandateId.value,
		documentType: DEFAULT_DOCUMENT_TYPE,
		documentVersion: DEFAULT_DOCUMENT_VERSION,
	};

	if (hasMappingsBeenLoaded.value) {
		const result = await refetchMappings(variables);
		if (result?.data?.AvalaraMappingFieldsList) {
			fieldMappings.value = result.data.AvalaraMappingFieldsList.map(
				(field, index) => {
					const optionality = normalizeOptionality(field.optionality);
					const avalaraFieldId =
						field.id || field.path || field.name || '';

					return {
						uiKey: `${String(
							avalaraFieldId || 'avalara-field'
						)}:${optionality}:${index}`,
						avalaraFieldId,
						avalaraFieldLabel:
							field.name || field.path || field.id || '',
						description: field.description || '',
						type: field.dataType || '',
						optionality,
						scoroField: '',
						isRequired: optionality === 'required',
						isExpanded: false,
					};
				}
			);
			applySavedMappingsToLoadedFields();
			autoPopulateFieldMappingFields();
		}
	} else {
		loadMappings(AvalaraMappingFieldsDocument, variables);
		hasMappingsBeenLoaded.value = true;
	}
};

watch(selectedMandateId, () => {
	loadMappingsForSelectedMandate();
});

const isEditMode = computed(() => {
	return editingMandateId.value !== undefined;
});

const modalTitle = computed(() => {
	const rawModule = String(props.module ?? '').trim();
	const singularModule =
		rawModule !== '' && rawModule.endsWith('s')
			? rawModule.slice(0, -1)
			: rawModule;

	const baseTitle = isEditMode.value
		? `Modify ${singularModule} mandate`
		: `Add ${singularModule} mandate`;

	const displayName = String(props.companyDisplayName ?? '').trim();
	if (displayName !== '') {
		return `${baseTitle} for ${displayName}`;
	}

	const companyName = String(props.companyName ?? '').trim();
	const fallbackCompanyName =
		companyName !== ''
			? `${companyName}${props.isSandbox ? ' (sandbox)' : ''}`
			: '';

	if (fallbackCompanyName === '') {
		return baseTitle;
	}

	return `${baseTitle} for ${fallbackCompanyName}`;
});

const modalTitlePrefix = computed(() => {
	const title = modalTitle.value;
	const companyName = String(props.companyName ?? '').trim();
	const displayName = String(props.companyDisplayName ?? '').trim();
	const companyNameFromDisplayName = displayName.endsWith(' (sandbox)')
		? displayName.slice(0, -' (sandbox)'.length).trim()
		: '';
	const companySegment =
		companyName !== '' ? companyName : companyNameFromDisplayName;
	if (companySegment === '') {
		return title;
	}

	const companyStart = title.indexOf(companySegment);
	if (companyStart === -1) {
		return title;
	}

	return title.slice(0, companyStart);
});

const modalTitleCompanyName = computed(() => {
	const title = modalTitle.value;
	const companyName = String(props.companyName ?? '').trim();
	const displayName = String(props.companyDisplayName ?? '').trim();
	const companyNameFromDisplayName = displayName.endsWith(' (sandbox)')
		? displayName.slice(0, -' (sandbox)'.length).trim()
		: '';
	const companySegment =
		companyName !== '' ? companyName : companyNameFromDisplayName;
	if (companySegment === '') {
		return '';
	}

	const companyStart = title.indexOf(companySegment);
	if (companyStart === -1) {
		return '';
	}

	return companySegment;
});

const modalTitleSuffix = computed(() => {
	const title = modalTitle.value;
	const companyName = String(props.companyName ?? '').trim();
	const displayName = String(props.companyDisplayName ?? '').trim();
	const companyNameFromDisplayName = displayName.endsWith(' (sandbox)')
		? displayName.slice(0, -' (sandbox)'.length).trim()
		: '';
	const companySegment =
		companyName !== '' ? companyName : companyNameFromDisplayName;
	if (companySegment === '') {
		return '';
	}

	const companyStart = title.indexOf(companySegment);
	if (companyStart === -1) {
		return '';
	}

	return title.slice(companyStart + companySegment.length);
});

const resetForm = () => {
	mandateName.value = '';
	mandateCode.value = undefined;
	description.value = '';
	bookmarkId.value = undefined;
	selectedFieldMappingId.value = undefined;
	fieldMappings.value = [];
	mappingsSearchQuery.value = '';
	editingMandateId.value = undefined;
	mandateDbId.value = undefined;
	isSaving.value = false;
	hasAttemptedSave.value = false;
	pendingFormData.value = null;
	savedScoroFieldByAvalaraFieldId.value = {};
	customProperties.value = [];
	activationStatus.value = null;

	mappingsViewFilter.value = 'required';
};

const populateForm = (mandate: MandateFormData) => {
	editingMandateId.value = mandate.id;
	mandateDbId.value = mandate.dbId ?? mandate.id;

	if (mandatesFetched.value && scoroFieldsFetched.value) {
		populateFormInternal(mandate);
	} else {
		pendingFormData.value = mandate;
	}
};

watch(
	() => props.isOpen,
	(isOpen) => {
		if (isOpen) {
			if (closeTimeoutId !== null) {
				window.clearTimeout(closeTimeoutId);
				closeTimeoutId = null;
			}

			modalVisible.value = true;
			modalPhase.value = 'opening';
			window.requestAnimationFrame(() => {
				modalPhase.value = 'open';
			});

			// For bills module, bookmarks are not needed (bills are fetched by flow=in)
			const isBills = props.module === 'bills';
			if (isBills) {
				bookmarksFetched.value = true;
			}

			const needsLoading =
				!mandatesFetched.value ||
				!scoroFieldsFetched.value ||
				!bookmarksFetched.value;
			if (needsLoading) {
				isLoadingInitialData.value = true;
			}

			fetchMandates();
			fetchScoroFields();
			if (!isBills) {
				fetchBookmarks();
			}
			fetchDefaultFieldMappings();

			if (props.editingMandate) {
				populateForm(props.editingMandate);
			} else {
				resetForm();
				if (!needsLoading) {
					isLoadingInitialData.value = false;
				}
			}
		} else {
			if (modalVisible.value) {
				modalPhase.value = 'closing';
				closeTimeoutId = window.setTimeout(() => {
					modalVisible.value = false;
					modalPhase.value = 'closed';
					closeTimeoutId = null;
					resetForm();
					isLoadingInitialData.value = false;
				}, MODAL_FADE_DURATION_MS);
				return;
			}

			resetForm();
			isLoadingInitialData.value = false;
		}
	},
	{ immediate: true }
);

watch(
	() => props.editingMandate,
	(newMandate) => {
		if (props.isOpen && newMandate) {
			populateForm(newMandate);
		} else if (props.isOpen && !newMandate) {
			resetForm();
		}
	}
);

const isMandateSelected = computed(() => {
	return mandateCode.value !== undefined && mandateCode.value !== '';
});

const isSaveDisabled = computed(() => {
	const isLoading =
		isLoadingInitialData.value ||
		isMandatesLoading.value ||
		isMappingsLoading.value;
	return isLoading || isSaving.value;
});

const isMandateInvalid = computed(() => {
	return hasAttemptedSave.value && !mandateCode.value;
});

// Bills module doesn't require bookmark (fetches all inbound documents)
const isBillsModule = computed(() => props.module === 'bills');

const isBookmarkInvalid = computed(() => {
	if (isBillsModule.value) {
		return false;
	}
	return hasAttemptedSave.value && !bookmarkId.value;
});

// For bills, mandate code must be unique (one mapping per mandate)
const isMandateCodeDuplicate = computed(() => {
	if (
		!isBillsModule.value ||
		!hasAttemptedSave.value ||
		mandateCode.value === undefined
	) {
		return false;
	}

	const selectedCode = String(mandateCode.value ?? '').trim();
	if (selectedCode === '') {
		return false;
	}

	return props.existingMandates.some((mandate) => {
		const existingCode = String(mandate?.mandateCode ?? '').trim();
		if (existingCode === '' || existingCode !== selectedCode) {
			return false;
		}

		const sameById =
			mandate?.id !== undefined &&
			editingMandateId.value !== undefined &&
			mandate.id === editingMandateId.value;
		const sameByDbId =
			mandate?.dbId !== undefined &&
			mandateDbId.value !== undefined &&
			mandate.dbId === mandateDbId.value;

		return !sameById && !sameByDbId;
	});
});

const isFieldMappingInvalid = computed(() => {
	return hasAttemptedSave.value && !selectedFieldMappingId.value;
});

const mandateErrorMessage = computed(() => {
	if (isMandateInvalid.value) {
		return t('avalara_mandate_required');
	}
	if (isMandateCodeDuplicate.value) {
		return billMandateAlreadyUsedMessage.value;
	}
	return undefined;
});

const onClose = () => {
	isSaving.value = false;
	emit('close');
};

const onSave = () => {
	hasAttemptedSave.value = true;
	if (
		isLoadingInitialData.value ||
		isMandatesLoading.value ||
		isMappingsLoading.value ||
		isSaving.value
	) {
		return;
	}

	const isBookmarkRequired = !isBillsModule.value;
	if (
		mandateName.value.trim() === '' ||
		!mandateCode.value ||
		(isBookmarkRequired && !bookmarkId.value) ||
		!selectedFieldMappingId.value
	) {
		return;
	}

	if (isNameDuplicate.value) {
		return;
	}

	if (isMandateCodeDuplicate.value) {
		return;
	}

	isSaving.value = true;
	if (!mandateDbId.value) {
		mandateDbId.value = generateDbId();
	}

	const mappedFieldMappings: SavedFieldMapping[] = fieldMappings.value
		.map((mapping) => ({
			avalaraFieldId: String(mapping.avalaraFieldId ?? '').trim(),
			avalaraFieldLabel: String(mapping.avalaraFieldLabel ?? '').trim(),
			avalaraField: String(mapping.avalaraFieldLabel ?? '').trim(),
			scoroField: String(mapping.scoroField ?? '').trim(),
		}))
		.filter(
			(mapping) =>
				(mapping.avalaraFieldId ?? '') !== '' &&
				mapping.scoroField !== ''
		);

	const bookmarkName = bookmarkId.value
		? (bookmarkOptions.value.find(
				(option) => option.id === bookmarkId.value
			)?.title ?? null)
		: null;

	emit('save', {
		id: editingMandateId.value,
		dbId: mandateDbId.value,
		name: mandateName.value,
		mandateCode: mandateCode.value ?? '',
		description: description.value,
		bookmarkId: bookmarkId.value,
		bookmarkName,
		fieldMappings: mappedFieldMappings,
		fieldMappingId: selectedFieldMappingId.value ?? null,
		activationStatus: activationStatus.value,
	});
};

const scoroFieldSearch = ref<Record<string, string>>({});
const scoroDropdownOpen = ref<Record<string, boolean>>({});

const getScoroFieldTitle = (fieldId: string | undefined): string => {
	if (!fieldId) {
		return '';
	}
	for (const groupData of groupedScoroFieldOptions.value) {
		const field = groupData.fields.find((f) => f.id === fieldId);
		if (field) {
			return field.title ?? '';
		}
	}
	if (fieldId.includes('.')) {
		const fieldName = fieldId.split('.').pop() ?? '';
		for (const groupData of groupedScoroFieldOptions.value) {
			const field = groupData.fields.find((f) => {
				// Match by field name part of the ID
				const fName =
					String(f.id ?? '')
						.split('.')
						.pop() ?? '';
				return fName === fieldName;
			});
			if (field) {
				return field.title ?? '';
			}
		}
	}
	return '';
};

/**
 * Get the placeholder text for a Scoro field dropdown.
 * Returns the field title if the field exists in options, otherwise returns the default placeholder.
 */
const getScoroFieldPlaceholder = (
	fieldId: string | undefined,
	defaultPlaceholder: string
): string => {
	if (!fieldId) {
		return defaultPlaceholder;
	}
	// Only show field title as placeholder if field exists in options
	if (fieldExistsInOptions(fieldId)) {
		return getScoroFieldTitle(fieldId) || defaultPlaceholder;
	}
	return defaultPlaceholder;
};

const getFilteredScoroFieldGroups = (uiKey: string) => {
	const query = (scoroFieldSearch.value[uiKey] ?? '').toLowerCase().trim();
	if (!query) {
		return groupedScoroFieldOptions.value;
	}

	const filtered: Array<{ group: string; fields: ScoroFieldOption[] }> = [];
	for (const groupData of groupedScoroFieldOptions.value) {
		const matchingFields = groupData.fields.filter((field) =>
			(field.title ?? '').toLowerCase().includes(query)
		);
		if (matchingFields.length > 0) {
			filtered.push({ group: groupData.group, fields: matchingFields });
		}
	}
	return filtered;
};

const getSearchQuery = (uiKey: string): string => {
	return (scoroFieldSearch.value[uiKey] ?? '').trim();
};

function toggleDescription(uiKey: string): void {
	const mapping = fieldMappings.value.find((m) => m.uiKey === uiKey);
	if (mapping) {
		mapping.isExpanded = !mapping.isExpanded;
	}
}

function updateFieldMapping(
	uiKey: string,
	scoroField: string | undefined
): void {
	if (scoroField?.startsWith('__group_header__')) {
		return;
	}

	const index = fieldMappings.value.findIndex(
		(mapping) => mapping.uiKey === uiKey
	);
	if (index === -1) {
		return;
	}
	fieldMappings.value[index].scoroField = scoroField ?? '';
}

function openScoroDropdown(uiKey: string, currentFieldId?: string): void {
	scoroDropdownOpen.value[uiKey] = true;
	scoroFieldSearch.value[uiKey] = currentFieldId
		? getScoroFieldTitle(currentFieldId)
		: '';
}

function closeScoroDropdown(uiKey: string): void {
	scoroDropdownOpen.value[uiKey] = false;
}

function selectScoroField(uiKey: string, fieldId: string): void {
	updateFieldMapping(uiKey, fieldId);
	scoroFieldSearch.value[uiKey] = '';
	closeScoroDropdown(uiKey);
	(document.activeElement as HTMLElement)?.blur();
}

function clearScoroField(uiKey: string, event: Event): void {
	event.stopPropagation();
	updateFieldMapping(uiKey, '');
	scoroFieldSearch.value[uiKey] = '';
}

const canAddCustomProperty = (uiKey: string): boolean => {
	const query = getSearchQuery(uiKey);
	if (!query) {
		return false;
	}
	return !customProperties.value.includes(query);
};

const addCustomProperty = (uiKey: string) => {
	const name = (scoroFieldSearch.value[uiKey] ?? '').trim();
	if (!name) {
		return;
	}
	if (!customProperties.value.includes(name)) {
		customProperties.value.push(name);
	}
	selectScoroField(uiKey, `custom:${name}`);
};

const onScoroFieldSearchInput = (uiKey: string, event: Event) => {
	scoroFieldSearch.value[uiKey] = (event.target as HTMLInputElement).value;
};

const onMandateChange = (value: string | undefined) => {
	const previousValue = mandateCode.value;

	mandateCode.value = value;
	syncActivationStatusWithSelectedMandate();

	if (!value) {
		savedScoroFieldByAvalaraFieldId.value = {};
		fieldMappings.value = [];
		customProperties.value = [];
		mappingsSearchQuery.value = '';
		activationStatus.value = null;
		return;
	}

	if (value !== previousValue) {
		savedScoroFieldByAvalaraFieldId.value = {};
		customProperties.value = [];
		mappingsSearchQuery.value = '';
		return;
	}

	loadMappingsForSelectedMandate();
};

const selectedBookmarkItem = computed<SelectItem | undefined>(() => {
	if (!bookmarkId.value) {
		return undefined;
	}

	return bookmarkOptions.value.find(
		(option) => option.id === bookmarkId.value
	);
});

const isBookmarksLabelVisible = computed(() => {
	return Boolean(selectedBookmarkItem.value);
});

const isBookmarkEmpty = computed(() => {
	return !isBookmarksLabelVisible.value;
});

// Field mapping dropdown options
const fieldMappingSelectOptions = computed<SelectItem[]>(() => {
	return props.fieldMappingOptions.map((fm) => ({
		id: fm.id,
		title: fm.name,
	}));
});

const isFieldMappingLabelVisible = computed(() => {
	return Boolean(selectedFieldMappingId.value);
});

const isFieldMappingEmpty = computed(() => {
	return !isFieldMappingLabelVisible.value;
});

const onFieldMappingChange = (value: string | undefined) => {
	const previousFieldMappingId = selectedFieldMappingId.value;
	selectedFieldMappingId.value = value;

	// Clear any scoroField values that were selected from the previous field mapping
	// This triggers when field mapping changes OR is cleared (value is undefined/empty)
	const wasCleared = !value || value === '';
	const wasChanged =
		previousFieldMappingId && previousFieldMappingId !== value;

	if (wasCleared || wasChanged) {
		for (const mapping of fieldMappings.value) {
			if (
				mapping.scoroField &&
				mapping.scoroField.startsWith('fieldmapping:')
			) {
				mapping.scoroField = '';
			}
		}
	}

	// Auto-populate after clearing
	if (value && value !== '') {
		autoPopulateFieldMappingFields();
	}
};

const onBookmarkSelected = (item: SelectItem) => {
	bookmarkId.value = item?.id ? String(item.id) : undefined;
};

const onBookmarkLoad = () => {
	if (bookmarksReloadTimeoutId !== null) {
		window.clearTimeout(bookmarksReloadTimeoutId);
	}

	bookmarksReloadTimeoutId = window.setTimeout(() => {
		bookmarksReloadTimeoutId = null;
		fetchBookmarks();
	}, BOOKMARKS_RELOAD_DEBOUNCE_MS);
};

const onConfirmDelete = () => {
	if (!editingMandateId.value) {
		return;
	}

	emit('delete', {
		id: editingMandateId.value,
		dbId: mandateDbId.value,
	});
};
</script>

<template>
	<SuiModal
		class="avalara-mandate-modal"
		:class="{
			'avalara-mandate-modal--open': modalPhase === 'open',
		}"
		:is-modal-open="modalVisible"
		:close-on-esc="false"
		@close="onClose"
		@keydown.enter.prevent
	>
		<template #header>
			<header class="avalara-mandate-modal__header">
				<div class="avalara-mandate-modal__header-left">
					<span class="avalara-mandate-modal__title">
						{{ t('avalara_mandates') }}
					</span>
					<span class="avalara-mandate-modal__breadcrumbs">
						<SuiIcon
							class="avalara-mandate-modal__breadcrumb-arrow"
							name="icon-arrow-right-16"
						/>
						<span class="avalara-mandate-modal__breadcrumb">
							<template v-if="modalTitleCompanyName"
								><span
									>{{ modalTitlePrefix
									}}<strong
										class="avalara-mandate-modal__breadcrumb-company"
										>{{ modalTitleCompanyName }}</strong
									>{{ modalTitleSuffix }}</span
								>
							</template>
							<template v-else>
								{{ modalTitle }}
							</template>
						</span>
					</span>
				</div>
			</header>
		</template>

		<div ref="modalContentRef" class="avalara-mandate-modal__content">
			<div
				v-if="isLoadingInitialData"
				class="avalara-mandate-modal__initial-loading-overlay"
			>
				<SuiLoadingCircle size="medium" />
			</div>

			<div class="avalara-mandate-modal__basic-fields">
				<div class="avalara-mandate-modal__row">
					<div
						class="avalara-mandate-modal__name-input"
						:class="{
							'avalara-mandate-modal__field--placeholder-overlay':
								isNameEmpty,
						}"
					>
						<span
							v-if="isNameEmpty"
							class="avalara-mandate-modal__placeholder-with-dot"
							aria-hidden="true"
						>
							<span
								class="avalara-mandate-modal__placeholder-text"
							>
								{{ t('name') }}
							</span>
							<span
								class="avalara-mandate-modal__placeholder-dot"
							>
								<svg
									xmlns="http://www.w3.org/2000/svg"
									width="4"
									height="4"
									viewBox="0 0 4 4"
								>
									<circle
										cx="2"
										cy="2"
										r="2"
										fill="currentColor"
									/>
								</svg>
							</span>
						</span>
						<span
							v-if="isNameLabelVisible"
							class="avalara-mandate-modal__field-floating-label"
						>
							{{ t('name') }}
						</span>
						<SuiInput
							v-model="mandateName"
							:placeholder="t('name')"
							data-testid="avalara-mandate-name"
							size="small"
							:variant="
								isNameInvalid || isNameDuplicate
									? SuiInputVariant.ERROR
									: undefined
							"
							:message="nameErrorMessage"
						/>
					</div>
					<div
						class="avalara-mandate-modal__mandate-select"
						:class="{
							'avalara-mandate-modal__field--placeholder-overlay':
								isMandateEmpty,
						}"
					>
						<span
							v-if="isMandateEmpty"
							class="avalara-mandate-modal__placeholder-with-dot"
							aria-hidden="true"
						>
							<span
								class="avalara-mandate-modal__placeholder-text"
							>
								{{ t('avalara_select_mandate') }}
							</span>
							<span
								class="avalara-mandate-modal__placeholder-dot"
							>
								<svg
									xmlns="http://www.w3.org/2000/svg"
									width="4"
									height="4"
									viewBox="0 0 4 4"
								>
									<circle
										cx="2"
										cy="2"
										r="2"
										fill="currentColor"
									/>
								</svg>
							</span>
						</span>
						<span
							v-if="isMandateLabelVisible"
							class="avalara-mandate-modal__field-floating-label"
						>
							{{ t('avalara_mandate') }}
						</span>
						<SuiSelect
							v-model="mandateCode"
							:placeholder="t('avalara_select_mandate')"
							:options="mandateOptions"
							:is-loading="isMandatesLoading"
							is-searchable
							enable-filtering
							:variant="
								isMandateInvalid || isMandateCodeDuplicate
									? SuiInputVariant.ERROR
									: undefined
							"
							:message="mandateErrorMessage"
							@update:model-value="onMandateChange"
						/>
						<SuiIcon
							v-if="isMandateInvalid || isMandateCodeDuplicate"
							class="avalara-mandate-modal__mandate-error-icon"
							name="icon-alert-16"
						/>
					</div>
				</div>

				<div class="avalara-mandate-modal__row">
					<div
						v-if="!isBillsModule"
						class="avalara-mandate-modal__bookmarks"
						:class="{
							'avalara-mandate-modal__bookmarks--floating':
								isBookmarksLabelVisible,
							'avalara-mandate-modal__field--placeholder-overlay':
								isBookmarkEmpty,
						}"
					>
						<span
							v-if="isBookmarkEmpty"
							class="avalara-mandate-modal__placeholder-with-dot"
							aria-hidden="true"
						>
							<span
								class="avalara-mandate-modal__placeholder-text"
							>
								{{ t('avalara_select_bookmark') }}
							</span>
							<span
								class="avalara-mandate-modal__placeholder-dot"
							>
								<svg
									xmlns="http://www.w3.org/2000/svg"
									width="4"
									height="4"
									viewBox="0 0 4 4"
								>
									<circle
										cx="2"
										cy="2"
										r="2"
										fill="currentColor"
									/>
								</svg>
							</span>
						</span>
						<span
							v-if="isBookmarksLabelVisible"
							class="avalara-mandate-modal__bookmarks-label"
						>
							{{ t('bookmark') }}
						</span>
						<AppAutoSuggest
							class="avalara-mandate-modal__bookmarks-autosuggest"
							:options="bookmarkOptions"
							:active-item="selectedBookmarkItem"
							:is-loading="isBookmarksLoading"
							:placeholder="t('avalara_select_bookmark')"
							data-testid="avalara-mandate-bookmark"
							:variant="
								isBookmarkInvalid
									? SuiInputVariant.ERROR
									: undefined
							"
							:message="
								isBookmarkInvalid
									? t('avalara_bookmark_required')
									: undefined
							"
							@input="onBookmarkSelected"
							@load="onBookmarkLoad"
						/>
					</div>
					<div
						class="avalara-mandate-modal__field-mapping-select"
						:class="{
							'avalara-mandate-modal__field-mapping-select--floating':
								isFieldMappingLabelVisible,
							'avalara-mandate-modal__field--placeholder-overlay':
								isFieldMappingEmpty,
							'avalara-mandate-modal__field-mapping-select--full-width':
								isBillsModule,
						}"
					>
						<span
							v-if="isFieldMappingEmpty"
							class="avalara-mandate-modal__placeholder-with-dot"
							aria-hidden="true"
						>
							<span
								class="avalara-mandate-modal__placeholder-text"
							>
								{{ t('avalara_field_mapping') }}
							</span>
							<span
								class="avalara-mandate-modal__placeholder-dot"
							>
								<svg
									xmlns="http://www.w3.org/2000/svg"
									width="4"
									height="4"
									viewBox="0 0 4 4"
								>
									<circle
										cx="2"
										cy="2"
										r="2"
										fill="currentColor"
									/>
								</svg>
							</span>
						</span>
						<span
							v-if="isFieldMappingLabelVisible"
							class="avalara-mandate-modal__field-floating-label"
						>
							{{ t('avalara_field_mapping') }}
						</span>
						<SuiSelect
							v-model="selectedFieldMappingId"
							:placeholder="t('avalara_field_mapping')"
							:options="fieldMappingSelectOptions"
							is-searchable
							enable-filtering
							:variant="
								isFieldMappingInvalid
									? SuiInputVariant.ERROR
									: undefined
							"
							:message="
								isFieldMappingInvalid
									? t('required_validation_error')
									: undefined
							"
							@update:model-value="onFieldMappingChange"
						/>
					</div>
				</div>
				<div class="avalara-mandate-modal__row">
					<div class="avalara-mandate-modal__description">
						<span
							v-if="isDescriptionLabelVisible"
							class="avalara-mandate-modal__field-floating-label"
						>
							{{ t('description') }}
						</span>
						<SuiTextarea
							v-model="description"
							:placeholder="t('description')"
							:rows="2"
						/>
					</div>
				</div>
				<div
					v-if="isBillsModule"
					class="avalara-mandate-modal__custom-info-message"
				>
					<div
						class="avalara-mandate-modal__custom-info-message-icon-container"
					>
						<SuiIcon
							class="avalara-mandate-modal__custom-info-message-icon"
							name="icon-info-filled-24"
						/>
					</div>
					<AppTranslatedTextWithHTML
						id="avalara_bills_line_mapping_info"
						class="avalara-mandate-modal__custom-info-message-text"
					/>
				</div>
			</div>

			<!-- Mappings section with loading overlay -->
			<div
				v-if="isMandateSelected"
				class="avalara-mandate-modal__mappings-section"
			>
				<!-- Loading overlay -->
				<div
					v-if="isMappingsLoading"
					class="avalara-mandate-modal__loading-overlay"
				>
					<SuiLoadingCircle size="medium" />
				</div>

				<div
					v-if="fieldMappings.length > 0"
					class="avalara-mandate-modal__mappings"
				>
					<div class="avalara-mandate-modal__mappings-header">
						<span
							class="avalara-mandate-modal__mappings-col avalara-mandate-modal__mappings-col--avalara"
						>
							<span
								class="avalara-mandate-modal__mappings-header-avalara"
							>
								<div
									class="avalara-mandate-modal__mappings-search"
								>
									<SuiInput
										v-model="mappingsSearchQuery"
										:placeholder="
											t('avalara_find_placeholder')
										"
										size="small"
									/>
									<SuiIcon
										name="icon-search-16"
										class="avalara-mandate-modal__search-icon"
									/>
								</div>
								<VDropdown
									:triggers="['click']"
									placement="bottom-start"
									:offset="[0, 4]"
									container="body"
									boundaries-element="body"
									theme="no-arrows"
								>
									<button
										type="button"
										class="avalara-mandate-modal__mappings-filter-dropdown"
									>
										<SuiIcon name="icon-eye-open-16" />
										<span
											class="avalara-mandate-modal__mappings-filter-text"
										>
											<span
												class="avalara-mandate-modal__mappings-filter-prefix"
											>
												{{ t('view') }}:
											</span>
											<span
												class="avalara-mandate-modal__mappings-filter-value"
											>
												{{ mappingsViewFilterLabel }}
											</span>
										</span>
										<SuiIcon
											name="icon-arrow-down-filled-16"
										/>
									</button>
									<template #popper="{ hide }">
										<div
											class="avalara-mandate-modal__mappings-filter-menu"
										>
											<button
												type="button"
												class="avalara-mandate-modal__mappings-filter-menu-item"
												:class="{
													'avalara-mandate-modal__mappings-filter-menu-item--active':
														mappingsViewFilter ===
														'all',
												}"
												@click="
													setMappingsViewFilter(
														'all',
														hide
													)
												"
											>
												{{ t('all') }}
											</button>
											<button
												type="button"
												class="avalara-mandate-modal__mappings-filter-menu-item"
												:class="{
													'avalara-mandate-modal__mappings-filter-menu-item--active':
														mappingsViewFilter ===
														'required',
												}"
												@click="
													setMappingsViewFilter(
														'required',
														hide
													)
												"
											>
												{{ t('required') }}
											</button>
											<button
												type="button"
												class="avalara-mandate-modal__mappings-filter-menu-item"
												:class="{
													'avalara-mandate-modal__mappings-filter-menu-item--active':
														mappingsViewFilter ===
														'conditional',
												}"
												@click="
													setMappingsViewFilter(
														'conditional',
														hide
													)
												"
											>
												{{ t('conditional') }}
											</button>
											<button
												type="button"
												class="avalara-mandate-modal__mappings-filter-menu-item"
												:class="{
													'avalara-mandate-modal__mappings-filter-menu-item--active':
														mappingsViewFilter ===
														'optional',
												}"
												@click="
													setMappingsViewFilter(
														'optional',
														hide
													)
												"
											>
												<span
													class="avalara-mandate-modal__capitalize"
												>
													{{ t('optional') }}
												</span>
											</button>
										</div>
									</template>
								</VDropdown>
							</span>
						</span>
						<span
							class="avalara-mandate-modal__mappings-col avalara-mandate-modal__mappings-col--type"
						>
							{{ t('type') }}
						</span>
						<span
							class="avalara-mandate-modal__mappings-col avalara-mandate-modal__mappings-col--scoro"
						>
							{{ t('scoro_field') }}
						</span>
					</div>

					<div
						v-for="mapping in visibleFieldMappings"
						:key="mapping.uiKey"
						class="avalara-mandate-modal__mappings-row"
						:class="{
							'avalara-mandate-modal__mappings-row--active':
								scoroDropdownOpen[mapping.uiKey],
							'avalara-mandate-modal__mappings-row--expanded':
								mapping.isExpanded,
						}"
					>
						<span
							class="avalara-mandate-modal__mappings-col avalara-mandate-modal__mappings-col--avalara"
						>
							<span
								class="avalara-mandate-modal__avalara-field-label"
							>
								<span class="avalara-mandate-modal__field-name">
									{{ mapping.avalaraFieldLabel }}
								</span>
								<span
									v-if="mapping.isRequired && !isBillsModule"
									class="avalara-mandate-modal__required-indicator"
								>
									*
								</span>
							</span>
							<span
								v-if="mapping.description"
								role="button"
								tabindex="0"
								class="avalara-mandate-modal__avalara-field-description"
								:class="{
									'avalara-mandate-modal__avalara-field-description--expanded':
										mapping.isExpanded,
								}"
								@click="toggleDescription(mapping.uiKey)"
								@keydown.enter.space.prevent="
									toggleDescription(mapping.uiKey)
								"
							>
								{{ mapping.description }}
							</span>
						</span>
						<span
							class="avalara-mandate-modal__mappings-col avalara-mandate-modal__mappings-col--type"
						>
							{{ mapping.type }}
						</span>
						<span
							class="avalara-mandate-modal__mappings-col avalara-mandate-modal__mappings-col--scoro"
						>
							<div
								class="avalara-mandate-modal__scoro-field-container"
								:class="{
									'avalara-mandate-modal__scoro-field-container--has-value':
										fieldExistsInOptions(
											mapping.scoroField
										),
								}"
							>
								<VDropdown
									:shown="scoroDropdownOpen[mapping.uiKey]"
									:triggers="[]"
									placement="bottom-start"
									:distance="0"
									:skidding="0"
									:auto-hide="false"
									:flip="false"
									:shift="true"
									:container="false"
									strategy="absolute"
									popper-class="avalara-scoro-field-popper"
									@hide="closeScoroDropdown(mapping.uiKey)"
								>
									<input
										type="text"
										class="avalara-mandate-modal__scoro-dropdown-input"
										:aria-label="
											t('scoro_field') +
											': ' +
											mapping.avalaraFieldLabel
										"
										:class="{
											'avalara-mandate-modal__scoro-dropdown-input--has-value':
												fieldExistsInOptions(
													mapping.scoroField
												) &&
												!scoroDropdownOpen[
													mapping.uiKey
												],
										}"
										:placeholder="
											getScoroFieldPlaceholder(
												mapping.scoroField,
												t('avalara_select_field')
											)
										"
										:value="
											scoroDropdownOpen[mapping.uiKey]
												? scoroFieldSearch[
														mapping.uiKey
													]
												: ''
										"
										@focus="
											openScoroDropdown(
												mapping.uiKey,
												mapping.scoroField
											)
										"
										@click="
											openScoroDropdown(
												mapping.uiKey,
												mapping.scoroField
											)
										"
										@blur="
											closeScoroDropdown(mapping.uiKey)
										"
										@input="
											onScoroFieldSearchInput(
												mapping.uiKey,
												$event
											)
										"
										@keydown.stop
									/>
									<template #popper>
										<div
											class="avalara-mandate-modal__scoro-dropdown-panel"
											@mousedown.prevent
										>
											<div
												class="avalara-mandate-modal__scoro-dropdown-list"
											>
												<div
													v-if="
														canAddCustomProperty(
															mapping.uiKey
														)
													"
													class="avalara-mandate-modal__scoro-dropdown-add"
												>
													<button
														type="button"
														@mousedown.prevent
														@click="
															addCustomProperty(
																mapping.uiKey
															)
														"
													>
														<svg
															class="sui-icon sui-icon--16"
															width="16"
															height="16"
														>
															<use
																href="#icon-plus-16"
															/>
														</svg>
														<span
															>Add new
															<strong>{{
																getSearchQuery(
																	mapping.uiKey
																)
															}}</strong></span
														>
													</button>
												</div>
												<template
													v-for="groupData in getFilteredScoroFieldGroups(
														mapping.uiKey
													)"
													:key="groupData.group"
												>
													<div
														class="avalara-mandate-modal__scoro-dropdown-group"
													>
														{{ groupData.group }}
													</div>
													<button
														v-for="field in groupData.fields"
														:key="field.id"
														type="button"
														class="avalara-mandate-modal__scoro-dropdown-item"
														:class="{
															'avalara-mandate-modal__scoro-dropdown-item--selected':
																mapping.scoroField ===
																field.id,
														}"
														@mousedown.prevent
														@click="
															selectScoroField(
																mapping.uiKey,
																String(field.id)
															)
														"
													>
														{{ field.title }}
													</button>
												</template>
											</div>
										</div>
									</template>
								</VDropdown>
								<button
									v-if="
										fieldExistsInOptions(mapping.scoroField)
									"
									type="button"
									class="avalara-mandate-modal__scoro-field-clear"
									tabindex="-1"
									@mousedown.prevent.stop="
										clearScoroField(mapping.uiKey, $event)
									"
								>
									<svg
										class="sui-icon sui-icon--16"
										width="16"
										height="16"
									>
										<use href="#icon-x-16" />
									</svg>
								</button>
							</div>
						</span>
					</div>
				</div>
			</div>
		</div>

		<template #footer>
			<footer class="avalara-mandate-modal__footer">
				<div class="avalara-mandate-modal__footer-grid">
					<div class="avalara-mandate-modal__footer-left">
						<div
							v-if="isEditMode"
							class="avalara-mandate-modal__delete-wrapper"
						>
							<AppConfirmationPopover
								v-slot="{ openConfirmation }"
								:title="t('avalara_delete_mandate')"
								message="general_delete_confirmation_message"
								@confirm="onConfirmDelete"
							>
								<button
									type="button"
									class="avalara-mandate-modal__delete-btn"
									:title="t('avalara_delete_mandate')"
									data-testid="avalara-mandate-modal-delete"
									@click="openConfirmation"
								>
									<SuiIcon name="icon-bin-16" />
								</button>
							</AppConfirmationPopover>
						</div>
					</div>

					<div class="avalara-mandate-modal__footer-center">
						<SuiButton
							variant="primary"
							:is-disabled="isSaveDisabled"
							:is-loading="isSaving"
							data-testid="avalara-mandate-modal-apply"
							@click="onSave"
						>
							{{ t('apply') }}
						</SuiButton>
					</div>

					<div class="avalara-mandate-modal__footer-right" />
				</div>
			</footer>
		</template>
	</SuiModal>
</template>

<style lang="scss" scoped>
.avalara-mandate-modal {
	--sui-modal-max-width: 800px;
	--sui-modal-min-width: 800px;

	:deep(.popup-modal__container) {
		background: rgb(31 34 38 / 15%);
		opacity: 0;
		transition: opacity 0.15s ease;
	}

	:deep(.popup-modal__item) {
		position: relative;
		background: $white;
		border-radius: 0.5rem;
		overflow: hidden;
		box-shadow:
			0 2px 6px 0 rgb(31 34 38 / 8%),
			0 8px 32px 0 rgb(31 34 38 / 12%);
		opacity: 0;
		transform: translateY(0.5rem) scale(0.98);
		transition:
			opacity 0.15s ease,
			transform 0.15s ease;
		will-change: opacity, transform;
	}

	&--open {
		:deep(.popup-modal__container) {
			opacity: 1;
		}

		:deep(.popup-modal__item) {
			opacity: 1;
			transform: translateY(0) scale(1);
		}
	}

	&__header {
		display: flex;
		align-items: center;
		width: 100%;
	}

	&__header-left {
		display: inline-flex;
		align-items: center;
		gap: 1rem;
	}

	&__breadcrumbs {
		display: inline-flex;
		align-items: center;
		gap: 1rem;
		padding-top: 0.25rem;
	}

	&__title {
		font-size: 2rem;
		font-weight: $font-weight-bold;
		line-height: 2.75rem;
		color: $neutral-90;
	}

	&__breadcrumb-arrow {
		color: $neutral-70;
	}

	&__breadcrumb {
		font-size: 1.75rem;
		line-height: 2rem;
		font-weight: 400;
		color: $neutral-70;
	}

	&__breadcrumb-company {
		font-weight: 600;
		color: $neutral-70;
	}

	&__content {
		display: flex;
		flex-direction: column;
		gap: 3rem;
		position: relative;
		min-height: 15rem;
	}

	:deep(.popup-modal__header),
	:deep(.popup-modal__content),
	:deep(.popup-modal__footer) {
		border-radius: 0;
	}

	:deep(.popup-modal__header) {
		background: $neutral-10;
		border-bottom: 1px solid $neutral-20;
		padding: 2rem 2rem 2rem 3rem;
		min-height: 7rem;
	}

	:deep(.popup-modal__content) {
		background: $white;
		padding: 3rem;
	}

	:deep(.popup-modal__footer) {
		background: $neutral-10;
		padding: 2rem;
		min-height: 8rem;
	}

	:deep(.popup-modal__close) {
		top: 2rem;
		right: 2rem;
		width: 3rem;
		height: 3rem;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	&__initial-loading-overlay {
		position: absolute;
		inset: 0;
		background: rgb(255 255 255 / 90%);
		display: flex;
		align-items: center;
		justify-content: center;
		z-index: 20;
		border-radius: 0.5rem;
	}

	&__basic-fields {
		display: flex;
		flex-direction: column;
		gap: 2rem;
	}

	&__basic-fields :deep(.sui-input__field),
	&__basic-fields :deep(.sui-auto-suggest__field),
	&__basic-fields :deep(.sui-select__field) {
		padding: 1rem;
	}

	&__basic-fields :deep([data-testid='sui-select__input']) {
		padding: 1rem;
	}

	&__basic-fields :deep(input),
	&__basic-fields :deep(textarea),
	&__basic-fields :deep([data-testid='sui-select__input']) {
		font-family: Inter, sans-serif;
		font-size: 1.5rem;
		font-style: normal;
		font-weight: 400;
		line-height: 2rem;
		color: $neutral-90;
	}

	&__basic-fields :deep(input::placeholder),
	&__basic-fields :deep(textarea::placeholder) {
		color: $neutral-70;
		font-family: Inter, sans-serif;
		font-size: 1.5rem;
		font-style: normal;
		font-weight: 400;
		line-height: 2rem;
		opacity: 1;
	}

	&__basic-fields :deep(.sui-input__placeholder-label),
	&__basic-fields :deep(.sui-auto-suggest__placeholder-label),
	&__basic-fields :deep(.sui-select__placeholder-label) {
		color: $neutral-70; // #717A89
		font-family: Inter, sans-serif;
		font-size: 1.5rem;
		font-style: normal;
		font-weight: 400;
		line-height: 2rem;
	}

	&__row {
		display: flex;
		gap: 3rem;
		align-items: center;
	}

	&__row--stacked {
		flex-direction: column;
		align-items: stretch;
		gap: 2rem;
	}

	&__name-input {
		flex: 1;
		position: relative;

		:deep(.sui-input) {
			width: 100%;
		}
	}

	&__mandate-select {
		width: 30rem;
		position: relative;

		:deep(.sui-select) {
			width: 100%;
		}

		// When mandate is invalid, hide the select's dropdown chevron (not the clear button)
		&:has(.avalara-mandate-modal__mandate-error-icon) {
			:deep(.sui-input__action-icon:not(.sui-input__action-icon--clear)) {
				display: none;
			}
		}

		// On hover, hide error icon and show clear button
		&:hover {
			.avalara-mandate-modal__mandate-error-icon {
				display: none;
			}

			// Show the clear button on hover when error icon is present
			&:has(.avalara-mandate-modal__mandate-error-icon) {
				:deep(.sui-input__action-icon--clear) {
					display: flex;
				}
			}
		}
	}

	&__mandate-error-icon {
		position: absolute;
		top: 50%;
		right: 1rem;
		transform: translateY(-50%);
		color: $red-60 !important;
		fill: $red-60 !important;
		z-index: 10;
		pointer-events: none;

		:deep(svg) {
			color: $red-60 !important;
			fill: $red-60 !important;
		}

		:deep(path) {
			fill: $red-60 !important;
		}
	}

	&__description {
		flex: 1;
		position: relative;
		width: 100%;
	}

	&__description :deep(textarea) {
		padding: 1rem;
		min-height: 5rem;
		height: 5rem;
		box-sizing: border-box;
		width: 100%;
		resize: vertical;
		border-radius: 0.5rem;
	}

	&__field-floating-label {
		position: absolute;
		left: 0.875rem;
		top: -0.75rem;
		padding: 0 0.5rem;
		background: $white;
		font-size: 1.25rem;
		line-height: 1.75rem;
		color: $neutral-70;
		font-weight: 400;
		z-index: 2;
		pointer-events: none;
	}

	&__bookmarks {
		flex: 1;
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
		position: relative;
	}

	&__bookmarks-label {
		position: absolute;
		left: 1rem;
		top: -0.75rem;
		padding: 0 0.5rem;
		background: $white;
		font-size: 1.25rem;
		color: $neutral-70;
		font-weight: 400;
		line-height: 1.5rem;
		z-index: 2;
	}

	&__bookmarks-autosuggest {
		width: 100%;
	}

	&__field-mapping-select {
		width: 30rem;
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
		position: relative;

		:deep(.sui-select) {
			width: 100%;
		}
	}

	// Bills: Field mapping should span full row width (bookmark is hidden).
	&__field-mapping-select--full-width {
		width: 100%;
		flex: 1;
	}

	&__field-mapping-select--floating {
		.sui-select__label {
			display: none;
		}
	}

	&__toggle-row {
		display: flex;
		align-items: center;
		gap: 0.5rem;
	}

	&__toggle-row :deep(.sui-toggle) {
		display: inline-flex;
		align-items: center;
	}

	&__toggle-row :deep(.sui-toggle__label) {
		display: inline-flex;
		align-items: center;
		font-family: Inter, sans-serif;
		font-size: 1.5rem;
		font-style: normal;
		font-weight: 400;
		line-height: 2rem;
		color: #000;
	}

	&__info-icon-wrapper {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		align-self: center;
	}

	&__info-icon {
		color: $neutral-60;
	}

	&__custom-info-message {
		display: flex;
		align-items: stretch;
		background-color: $blue-10;
		border: 1px solid $blue-30;
		border-left: 4px solid $blue-50;
		border-radius: 0.5rem;
		margin-top: 1rem;
		overflow: hidden;
	}

	&__custom-info-message-icon-container {
		display: flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
		padding: 1.5rem;
		background-color: rgba($blue-60, 0.08);
	}

	&__custom-info-message-icon {
		color: $blue-50;
		fill: $blue-50;

		:deep(svg) {
			color: $blue-50;
			fill: $blue-50;
		}

		:deep(path) {
			fill: $blue-50;
		}
	}

	&__custom-info-message-text {
		flex: 1;
		padding: 1.5rem;
		font-family: Inter, sans-serif;
		font-size: 1.5rem;
		font-weight: 400;
		line-height: 2rem;
		color: $neutral-90;

		:deep(a) {
			color: $blue-50;
			text-decoration: none;

			&:hover {
				color: $blue-60;
			}
		}
	}

	&__mappings-section {
		position: relative;
		display: flex;
		flex-direction: column;
		gap: 3rem;
	}

	&__loading-overlay {
		position: absolute;
		inset: 0;
		background: rgb(255 255 255 / 80%);
		display: flex;
		align-items: center;
		justify-content: center;
		z-index: 10;
		border-radius: 0.5rem;
	}

	&__mappings {
		display: flex;
		flex-direction: column;
	}

	&__capitalize {
		text-transform: capitalize;
	}

	&__mappings-header {
		display: flex;
		gap: 2rem;
		padding: 1.25rem 0;
		border-bottom: 1px solid $neutral-20;
	}

	&__mappings-row {
		display: flex;
		gap: 2rem;
		padding: 1.25rem 0;
		border-bottom: 1px solid $neutral-20;
		align-items: center;

		&:last-child {
			border-bottom: none;
		}

		&:hover {
			position: relative;
			z-index: 100;
		}

		&--expanded {
			position: relative;
			z-index: 200;

			.avalara-mandate-modal__avalara-field-description {
				white-space: normal;
				overflow-wrap: break-word;
			}
		}

		&--active,
		&--active:hover {
			position: relative;
			z-index: 300;
		}
	}

	&__mappings-col {
		font-family: Inter, sans-serif;
		font-size: 1.5rem;
		font-style: normal;
		font-weight: 400;
		line-height: 2rem;
		color: #000;
		overflow-wrap: break-word;

		&--avalara {
			flex: 1;
			display: flex;
			flex-direction: column;
			justify-content: center;
			gap: 0.5rem;
			min-width: 0;
		}

		&--type {
			width: 10rem;
			flex-shrink: 0;
		}

		&--scoro {
			width: 23rem;
			flex-shrink: 0;
			overflow: visible;
		}
	}

	&__mappings-header &__mappings-col {
		color: $neutral-70;
	}

	&__mappings-header-avalara {
		display: inline-flex;
		align-items: center;
		gap: 2rem;
		width: 100%;
	}

	&__mappings-search {
		width: 22rem;
		position: relative;
		display: flex;
		align-items: center;

		:deep(.sui-input) {
			width: 100%;
		}

		:deep(.sui-input__field) {
			height: 4rem;
			padding: 0 3.5rem 0 1rem !important;
		}
	}

	&__search-icon {
		position: absolute;
		right: 1rem;
		color: $neutral-60;
		pointer-events: none;
	}

	&__mappings-filter-dropdown {
		display: inline-flex;
		align-items: center;
		gap: 0.5rem;
		padding: 1rem;
		border: 1px solid $neutral-40;
		border-radius: 0.5rem;
		background: $white;
		cursor: pointer;

		&:focus {
			outline: none;
		}
	}

	&__mappings-filter-text {
		display: inline-flex;
		gap: 0.5rem;
		font-size: 1.5rem;
		line-height: 2rem;
	}

	&__mappings-filter-prefix {
		color: $neutral-70;
		font-weight: 400;
	}

	&__mappings-filter-value {
		color: $neutral-90;
		font-weight: 600;
	}

	&__mappings-filter-menu {
		background: $white;
		border-radius: 0.5rem;
		box-shadow:
			0 0 0.5rem rgb(73 79 89 / 8%),
			0 0.375rem 1rem rgb(73 79 89 / 5%);
		padding: 0.5rem 0;
		min-width: 18rem;
	}

	&__mappings-filter-menu-item {
		width: 100%;
		height: 4rem;
		padding: 0 2rem;
		background: transparent;
		border: none;
		text-align: left;
		cursor: pointer;
		font-family: Inter, sans-serif;
		font-size: 1.5rem;
		line-height: 2rem;
		color: $neutral-90;

		&:hover {
			background: $neutral-10;
		}

		&--active {
			font-weight: 600;
		}
	}

	&__field-name {
		font-family: Inter, sans-serif;
		font-size: 1.5rem;
		font-style: normal;
		font-weight: 600;
		line-height: 2rem;
		color: $neutral-90;
		overflow-wrap: break-word;
		word-break: break-word;
		max-width: 100%;
	}

	&__avalara-field-label {
		display: flex;
		align-items: flex-start;
		min-width: 0;
		max-width: 100%;
	}

	&__avalara-field-description {
		color: $neutral-80; // #494F59
		font-size: 1.5rem;
		line-height: 2rem;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		display: block;
		max-width: 100%;
		cursor: pointer;

		&--expanded {
			white-space: normal;
			overflow-wrap: break-word;
		}
	}

	&__required-indicator {
		color: $orange-50;
		margin-left: 0.25rem;
	}

	&__field--placeholder-overlay {
		:deep(input::placeholder) {
			opacity: 0;
		}

		:deep(textarea::placeholder) {
			opacity: 0;
		}

		&:focus-within,
		&:has(input:not(:placeholder-shown)),
		&:has(textarea:not(:placeholder-shown)) {
			.avalara-mandate-modal__placeholder-with-dot {
				opacity: 0;
			}
		}
	}

	&__placeholder-with-dot {
		position: absolute;
		left: 1rem;
		top: 50%;
		transform: translateY(-50%);
		display: inline-flex;
		align-items: flex-start;
		gap: 0.5rem;
		pointer-events: none;
		z-index: 3;
	}

	&__placeholder-text {
		color: $neutral-70;
		font-family: Inter, sans-serif;
		font-size: 1.5rem;
		font-style: normal;
		font-weight: 400;
		line-height: 2rem;
	}

	&__placeholder-dot {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 0.5rem;
		height: 0.5rem;
		color: $orange-50;
		margin-top: 0.25rem;
	}

	&__field-select {
		width: 100%;
	}

	&__scoro-field-container {
		position: relative;
		width: 100%;
		overflow: visible;

		> .v-popper {
			width: 100%;
			overflow: visible;
		}

		&--has-value:hover {
			.avalara-mandate-modal__scoro-field-clear {
				opacity: 1;
				pointer-events: auto;
			}

			.avalara-mandate-modal__scoro-dropdown-input {
				background-image: none;
			}
		}
	}

	&__scoro-dropdown-input {
		width: 100%;
		height: 4rem;
		padding: 0 2.8rem 0 1rem;
		font-family: Inter, sans-serif;
		font-size: 1.5rem;
		line-height: 2rem;
		color: $neutral-90;
		background-color: $white;
		background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='5' viewBox='0 0 8 5'%3E%3Cpath fill='%23A3ABB8' d='M4 5L0 0h8L4 5z'/%3E%3C/svg%3E");
		background-repeat: no-repeat;
		background-position: right 1rem center;
		background-size: 1rem;
		border: 1px solid $neutral-40;
		border-radius: 0.5rem;
		outline: none;
		cursor: text;

		&::placeholder {
			color: $neutral-50;
		}

		&--has-value::placeholder {
			color: $neutral-90;
		}

		&:hover {
			border-color: $neutral-50;
		}
	}

	&__scoro-field-clear {
		position: absolute;
		top: 0;
		right: 0;
		bottom: 0;
		z-index: 10;
		display: flex;
		align-items: center;
		justify-content: center;
		width: 3rem;
		padding: 0;
		background: none;
		border: none;
		cursor: pointer;
		color: $neutral-50;
		opacity: 0;
		pointer-events: none;

		&:hover {
			color: $neutral-70;
		}

		svg {
			width: 16px;
			height: 16px;
			display: block;
		}
	}

	&__scoro-dropdown-panel {
		width: 23rem;
		box-sizing: border-box;
		background-color: $white;
		border-radius: 0.5rem;
		box-shadow: 0 4px 16px rgb(0 0 0 / 16%);
		overflow: hidden;
	}

	&__scoro-dropdown-list {
		max-height: 40rem;
		overflow-y: auto;
		padding: 0.5rem 0;
	}

	&__scoro-dropdown-group {
		padding: 0.75rem 1rem 0.5rem;
		font-family: Inter, sans-serif;
		font-size: 1.5rem;
		font-weight: 600;
		line-height: 2rem;
		color: $neutral-60;
		border-bottom: 1px solid $neutral-20;
	}

	&__scoro-dropdown-item {
		display: block;
		width: 100%;
		padding: 0.75rem 1rem 0.75rem 1.5rem;
		font-family: Inter, sans-serif;
		font-size: 1.5rem;
		line-height: 2rem;
		color: $neutral-90;
		background: none;
		border: none;
		border-bottom: 1px solid $neutral-20;
		cursor: pointer;
		text-align: left;

		&:hover {
			background-color: $neutral-10;
		}

		&--selected {
			background-color: $blue-10;
			color: $blue-60;
		}

		&:last-child {
			border-bottom: none;
		}
	}

	&__scoro-dropdown-empty {
		padding: 1rem;
		font-family: Inter, sans-serif;
		font-size: 1.5rem;
		color: $neutral-60;
		text-align: center;
	}

	&__scoro-dropdown-add {
		padding: 0.5rem;

		button {
			display: flex;
			align-items: center;
			gap: 0.5rem;
			width: 100%;
			padding: 0.5rem;
			background: none;
			border: none;
			border-radius: 0.5rem;
			cursor: pointer;
			font-family: Inter, sans-serif;
			font-size: 1.5rem;
			color: $blue-60;
			text-align: left;

			&:hover {
				background-color: $neutral-10;
			}

			svg {
				flex-shrink: 0;
			}

			strong {
				font-weight: 600;
			}
		}
	}

	&__footer {
		width: 100%;
	}

	&__footer-grid {
		display: grid;
		grid-template-columns: 1fr auto 1fr;
		align-items: center;
		width: 100%;
	}

	&__footer-left {
		display: flex;
		justify-content: flex-start;
	}

	&__footer-center {
		display: flex;
		justify-content: center;
	}

	&__delete-wrapper {
		position: relative;
		display: inline-flex;
	}

	&__delete-btn {
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 1rem;
		border: none;
		background: transparent;
		cursor: pointer;
		color: $neutral-60;

		&:hover {
			color: $red-60;
		}
	}
}
</style>

<style lang="scss">
.avalara-scoro-field-popper {
	&.v-popper--theme-dropdown {
		.v-popper__inner {
			width: 23rem;
			max-width: 23rem;
		}
	}
}
</style>
